import { createUserNameFromEmail } from '@sv-support-sdk/utils/src/helpers/string';
import { ticketsActions } from '@sv-support-sdk/store';
import { IAttachment } from '@sv-support-sdk/shared-types/src/common/tickets';
import { IFetchTicketsParams } from '@sv-support-sdk/shared-types/src/api/tickets';

import { TThunk } from '../index';
import { ticketsActions as ticketsSliceActions } from '../slices/tickets';
import { modalActions as modalActionsSlices } from '../slices/modals';
import { CONTACT_US_MODAL, THANK_YOU_MODAL } from '../../constants/modals';
import notificationActions from './notifications';
import { $l } from '../../locales';

const getTickets =
  (params: IFetchTicketsParams = {}, loadMore?: boolean): TThunk =>
  async (dispatch, getState, api) => {
    const {
      tickets: { loading, listMeta },
    } = getState();

    const paramsToSend = params;

    if (loading.list) return;

    if (loadMore) {
      const remainTickets = listMeta.total - listMeta.currentPage * Number(listMeta.perPage);

      if (remainTickets > 0) {
        paramsToSend.page = listMeta.currentPage + 1;
      } else {
        return;
      }
    }

    dispatch(ticketsSliceActions.setTicketsLoading(true));

    const {
      tickets: { listMeta: ticketsMeta, list },
    } = getState();

    const readyToSendParams = {
      page: ticketsMeta.currentPage,
      per_page: Number(ticketsMeta.perPage),
      order_by_direction: ticketsMeta.orderByDirection,
      ...(paramsToSend || {}),
    };

    try {
      const { data, perPage, total, hash } = await api.tickets.fetchTickets(readyToSendParams);

      dispatch(
        ticketsSliceActions.setTickets(readyToSendParams.page === 1 ? data : [...list, ...data]),
      );
      dispatch(
        ticketsSliceActions.setTicketsMeta({
          perPage,
          total,
          hash,
          currentPage: readyToSendParams.page,
          orderByDirection: readyToSendParams.order_by_direction,
        }),
      );
    } finally {
      dispatch(ticketsSliceActions.setTicketsLoading(false));
    }
  };

const getOneTicket =
  (ticketId: string, hash?: string): TThunk =>
  async (dispatch, getState, api) => {
    const {
      user: { avatar },
    } = getState();

    dispatch(ticketsSliceActions.setSelectedTicketLoading(true));

    try {
      const data = await api.tickets.fetchOneTicket(ticketId, { hash });
      const { initialMessage, unread } = data;
      const { name, email } = initialMessage.author;

      const alteredInitialMessage = {
        ...initialMessage,
        author: {
          ...initialMessage.author,
          name: email && !name ? createUserNameFromEmail(email) : name,
          avatar:
            initialMessage.author.type === 'User'
              ? avatar || undefined
              : initialMessage.author.avatar,
        },
      };

      dispatch(
        ticketsSliceActions.setSelectedTicket({
          ...data,
          initialMessage: alteredInitialMessage,
        }),
      );

      if (unread) {
        dispatch(ticketsActions.markMessagesAsRead(Number(ticketId), hash || '', 1));
      }
    } catch {
      dispatch(
        notificationActions.notifyError({
          title: $l('ALERTS.WARNING_TITLE'),
          message: $l('ALERTS.SOMETHING_WENT_WRONG'),
        }),
      );
    } finally {
      dispatch(ticketsSliceActions.setSelectedTicketLoading(false));
    }
  };

const replyToTicketAction =
  (
    ticketId: string,
    params: { hash: string },
    body: { message: string; attachments: IAttachment[] },
  ): TThunk =>
  async (dispatch, _, api) => {
    dispatch(ticketsSliceActions.setSelectedTicketLoading(true));

    try {
      const data = await api.tickets.replyToTicket(ticketId, params, {
        ticket_id: Number(ticketId),
        message: body.message,
        attachments: body.attachments,
      });

      dispatch(
        notificationActions.notifySuccess({
          title: $l('ALERTS.SUCCESS_TITLE'),
          message: $l('TICKETS.SUCCESS_REPLY_MESSAGE'),
        }),
      );

      dispatch(ticketsSliceActions.addMessageToSelectedTicket(data));
      dispatch(ticketsSliceActions.updateSelectedTicket({ closedAt: null }));
    } catch (e) {
      dispatch(
        notificationActions.notifyError({
          title: $l('ALERTS.WARNING_TITLE'),
          message: $l('ALERTS.SOMETHING_WENT_WRONG'),
        }),
      );
    } finally {
      dispatch(ticketsSliceActions.setSelectedTicketLoading(false));
    }
  };

const createTicket =
  (ticket: {
    category?: number;
    email: string;
    message: string;
    subject: string;
    org_id?: string;
    attachments: IAttachment[];
  }): TThunk =>
  async (dispatch, _, api) => {
    dispatch(ticketsSliceActions.setLoading({ key: 'createTicket', value: true }));

    try {
      const data = await api.tickets.createTicket(ticket);

      dispatch(ticketsSliceActions.addTicketToList(data));
      dispatch(
        modalActionsSlices.openModal({
          name: THANK_YOU_MODAL,
          props: { ticketId: data.id, ticketHash: data.hash },
        }),
      );

      dispatch(modalActionsSlices.closeModal(CONTACT_US_MODAL));
    } catch (e) {
      if (e instanceof Error && e.message) {
        dispatch(
          notificationActions.notifyError({
            title: $l('ALERTS.ERROR_TITLE'),
            message: e.message,
          }),
        );
      }
    } finally {
      dispatch(ticketsSliceActions.setLoading({ key: 'createTicket', value: false }));
    }
  };

export default {
  ...ticketsActions,
  ...ticketsSliceActions,
  createTicket,
  getOneTicket,
  getTickets,
  replyToTicketAction,
};
